import React, {useState} from 'react';

import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {gt} from 'app/utils/i18n';
import {getHtmlDiff} from 'app/utils/showdiff';

import type {Moment} from 'moment-tz';

export type EditionHistoryType = {
  source: 'me' | 'ai' | 'originalText';
  text: string;
  time: Moment | null;
};
export type EditionHistoryPanelProps = {
  editedText: EditionHistoryType;
  originalText: string;
  showPreviousVersion: () => void;
  canShowPreviousVersion: boolean;
  showNextVersion: () => void;
  canShowNextVersion: boolean;
};
export const EditionHistoryPanel = ({
  editedText,
  originalText,
  showPreviousVersion,
  canShowPreviousVersion,
  showNextVersion,
  canShowNextVersion,
}: EditionHistoryPanelProps) => {
  const [showChanges, setShowChanges] = useState(false);

  const textToShow = showChanges
    ? getHtmlDiff(originalText, editedText.text, false)
    : editedText.text;

  return (
    <div className="col-md-6">
      <div className="pre-scrollable vh-100 px-3 pb-5">
        <UserContentContainer
          content={
            typeof textToShow === 'string'
              ? textToShow
              : ReactDOMServer.renderToStaticMarkup(textToShow)
          }
        />
      </div>
      <StyledEditToolbarDiv className="d-flex justify-content-between align-items-center position-relative bg-white mr-3 pl-2">
        <div className="d-inline">
          <LazyTooltip tooltip={gt('Undo')}>
            <button
              onClick={showPreviousVersion}
              disabled={!canShowPreviousVersion}
              className="btn btn-link btn-sm text-dark"
            >
              <i className="fas fa-undo mr-1"></i>
            </button>
          </LazyTooltip>

          <LazyTooltip tooltip={gt('Redo')}>
            <button
              onClick={showNextVersion}
              disabled={!canShowNextVersion}
              className="btn btn-link btn-sm text-dark"
            >
              <i className="fas fa-redo ml-1"></i>
            </button>
          </LazyTooltip>
        </div>
        <div className="d-inline">
          {editedText.source === 'ai' ? (
            <LazyTooltip tooltip={gt('Generated by SweetAI')}>
              <i className="fas fa-robot mr-2" />
            </LazyTooltip>
          ) : editedText.source === 'originalText' ? (
            <LazyTooltip tooltip={gt('Original text')}>
              <i className="fas fa-user-alt mr-2" />
            </LazyTooltip>
          ) : (
            <LazyTooltip tooltip={gt('Original text')}>
              <i className="fas fa-user mr-2" />
            </LazyTooltip>
          )}
          {editedText.time != null ? (
            <LazyTooltip
              tooltip={gt('Generated at: %s', editedText.time?.format('lll'))}
            >
              <i className="far fa-clock"></i>
            </LazyTooltip>
          ) : null}
        </div>
        <button
          onClick={() => setShowChanges(!showChanges)}
          className="btn btn-link btn-sm"
        >
          {showChanges ? gt('Hide Changes') : gt('Show Changes')}
        </button>
      </StyledEditToolbarDiv>
    </div>
  );
};

const StyledEditToolbarDiv = styled.div`
  margin-top: -2rem;
`;
